import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { binService } from "/app/src/services";
import { Bin as BinType } from "/app/src/models";
import { Search } from "/app/src/components/generic/components/search";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * Renders the bins explorer component which displays a table of bins with pagination, sorting, and search functionality.
 *
 * @returns {JSX.Element} The bins explorer component.
 */
export default function Bins() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<BinType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: () => t("translation:name"),
      }),
      columnHelper.accessor("width", {
        id: "width",
        cell: (info) => info.getValue(),
        header: () => t("translation:width"),
      }),
      columnHelper.accessor("height", {
        id: "height",
        cell: (info) => info.getValue(),
        header: () => t("translation:height"),
      }),
      columnHelper.accessor("depth", {
        id: "depth",
        cell: (info) => info.getValue(),
        header: () => t("translation:depth"),
      }),
    ],
    [columnHelper, t],
  );

  const [sort, setSort] = useSortUpgrade([]);

  const [searchParams] = useSearchParams();
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const goToBin = useCallback(
    (binId: number) => {
      navigate(`/explorer/bins/${binId}`);
    },
    [navigate],
  );

  const {
    data: bins,
    count,
    isFetching,
  } = usePaginatedData<BinType>({
    queryKey: ["bins"],
    searchString,
    sort,
    page,
    pageSize,
    service: binService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:bins")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={bins}
        tableColumns={columns}
        length={count}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        enableRowSelection
        rowClicked={goToBin}
        emptyText={t("translation:no_bins")}
      />
    </div>
  );
}
